/**
 * Boot-stall recovery.
 *
 * Field failure (2026-07-30, launch day): players across many Windows machines — one on
 * an RTX 4090 — stuck at "almost ready" forever. Their console showed every shader
 * compile failing with an EMPTY error log, fallbacks included, which is the signature of
 * a dead WebGL context, not a broken shader: once the context is lost (driver reset, GPU
 * process killed mid-compile), nothing compiles ever again, `scene.whenReadyAsync()`
 * never resolves, and the boot had no timeout — so the bar sat at "almost ready" until
 * the player gave up. The same build compiles clean on Metal, native GL and SwiftShader;
 * the loss is specific to machines we cannot test from here, so the fix is to absorb it.
 *
 * Recovery, not diagnosis: when the boot is judged stuck, reload once at the next
 * quality tier down. Medium drops the prepass/SSAO, antialiasing and the heavy shadow
 * filtering — the bulk of the shader work — so the second boot is a far smaller bite.
 * The ladder self-terminates at low; after that the only honest move is to say so.
 *
 * Engine-free so the timing contract is testable without booting a game.
 */

import type { QualityTier } from '../render/world';

/**
 * How long a boot may sit with every download finished but the scene still not ready
 * before it is judged stuck. Shader compilation for the whole scene takes single-digit
 * seconds on the slowest machine we have measured; 25 gives that a wide margin while
 * still rescuing a stuck player faster than they give up.
 */
export const SETTLED_STALL_MS = 25_000;

/**
 * Grace after a `webglcontextlost` event. Babylon handles restoration itself and a
 * restored context can still finish the boot; only a loss that stays lost is a stall.
 */
export const CONTEXT_LOST_STALL_MS = 10_000;

/**
 * The rung below the tier that failed. Anything unrecognised — including the default
 * (no `?quality=` at all, which the renderer treats as high) — retries at medium.
 * Low has no rung below it: the caller must tell the player the truth instead.
 */
export function recoveryTier(tried: string | null | undefined): QualityTier | null {
  if (tried === 'medium') return 'low';
  if (tried === 'low') return null;
  return 'medium';
}

/** 1-based rung number for telemetry: which attempt at booting this machine just failed. */
export function recoveryAttempt(tried: string | null | undefined): number {
  if (tried === 'medium') return 2;
  if (tried === 'low') return 3;
  return 1;
}

/** The same page, same save, one tier down. Every other param survives the reload. */
export function recoveryUrl(href: string, tier: QualityTier): string {
  const url = new URL(href);
  url.searchParams.set('quality', tier);
  return url.toString();
}

export interface StallDetector {
  /** Call every tick while all real work (assets and film) has settled. */
  noteWorkSettled(now: number): void;
  /** Call when the canvas fires `webglcontextlost`. */
  noteContextLost(now: number): void;
  /** True exactly once, at the moment the boot is judged stuck. */
  poll(now: number): boolean;
}

export function createStallDetector(): StallDetector {
  let settledAt: number | null = null;
  let lostAt: number | null = null;
  let fired = false;

  return {
    noteWorkSettled(now: number): void {
      if (settledAt === null) settledAt = now;
    },
    noteContextLost(now: number): void {
      if (lostAt === null) lostAt = now;
    },
    poll(now: number): boolean {
      if (fired) return false;
      const stuck =
        (lostAt !== null && now - lostAt >= CONTEXT_LOST_STALL_MS) ||
        (settledAt !== null && now - settledAt >= SETTLED_STALL_MS);
      if (stuck) fired = true;
      return stuck;
    },
  };
}
